import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LogIn, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (user) nav("/admin", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1581141444721-0e6f8fa8397e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODA4MTg2Mzd8MA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="text-sm uppercase tracking-[0.3em] text-amber-300">← Back to website</Link>
          <div>
            <div className="text-amber-300 text-6xl font-serif-en">ﷲ</div>
            <h1 className="font-serif-en text-5xl mt-4">Masjid Al Noor</h1>
            <p className="mt-3 text-stone-200/80 max-w-md">Manage prayer times, announcements, gallery, events and madrasa info from one secure dashboard.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-700 mb-3">
              <LogIn className="w-3.5 h-3.5" /> {t("nav_admin")}
            </div>
            <h2 className="font-serif-en text-3xl text-emerald-950">{t("welcome_back")}</h2>
            <p className="text-stone-500 mt-1 text-sm">{t("admin_subtitle")}</p>
          </div>

          <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">{t("email")}</label>
          <input
            data-testid="login-email-input"
            type="text" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username"
            className="w-full px-4 py-3 mb-4 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 bg-white"
            placeholder="masjidadmin"
          />
          <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1">{t("password")}</label>
          <input
            data-testid="login-password-input"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full px-4 py-3 mb-2 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 bg-white"
            placeholder="••••••••"
          />

          {err && (
            <div data-testid="login-error" className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mt-3">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            disabled={busy}
            className="btn-primary w-full mt-6 disabled:opacity-60"
          >
            {busy ? "..." : t("sign_in")}
          </button>
          <div className="text-center mt-4">
            <Link to="/admin/forgot-password" data-testid="forgot-link" className="text-sm text-emerald-900 hover:underline">Forgot password?</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
